import type { OperationsAttentionKey, OperationsIntakeSourceKey, OperationsJourneyCarrierKey, OperationsLensKey, OperationsPenKey, OperationsStageKey, OperationsWaitingOnDto, OperationsStartRiskKey } from "@/api/endpoints";

export const STAGE_ORDER: OperationsStageKey[] = [
  "requestProcessing",
  "releaseToSubmit",
  "submitToApprove",
];
/** Open, every date set, FBO status not yet Completed: no row on the card since 2026-10-22, counted in the note under it. */
export const HIDDEN_STAGE: OperationsStageKey = "awaitingClose";

export const STAGE_LABEL: Record<OperationsStageKey, string> = {
  requestProcessing: "Request Processing",
  releaseToSubmit: "Completed to WO Submit",
  submitToApprove: "WO Submit to Approve",
  awaitingClose: "Awaiting close",
};

/** Tile order (Rafael, 2026-10-21): four exclusive tiles that add up to Open now; Unknown appears only when it is not 0. */
export const ATTENTION_ORDER: OperationsAttentionKey[] = ["stuck", "blocked", "stuckAndBlocked", "clear"];

export const ATTENTION_LABEL: Record<OperationsAttentionKey, string> = {
  stuck: "Stuck",
  blocked: "Blocked",
  stuckAndBlocked: "Stuck + Blocked",
  clear: "Clear",
  unknown: "Unknown",
};

/** Lens control and tag wording. */
export const LENS_LABEL: Record<OperationsLensKey, string> = { stuck: "Stuck", blocked: "Blocked", stuckAndBlocked: "Stuck + Blocked", clear: "Clear" };

export const LENS_TIP =
  "Show only one attention state on Open now, the stages, the age card and the owners. Started / ended, arrivals and the tiles always show everything. Combines with Who holds the pen.";

/** Who holds the pen (2026-10-28): the second lens, cutting the same cards as the Attention lens. */
export const PEN_LABEL: Record<OperationsPenKey, string> = { internal: "Internal", external: "External" };
export const PEN_TIP =
  "Look at the open work by who holds the pen. External = the first open task is with someone outside Procom: a Consultant, a Subvendor worker or a Subvendor organisation. Internal = everything else: Client Service, Account Manager, Internal and other roles, the work order waiting to be submitted or approved, a request with no pending action, and every intake. Cuts the same cards as the Attention lens and combines with it; started / ended, arrivals and the tiles always show everything.";

/** "Stuck · External", "External", "Stuck"; null without a lens. The tags, the stamp and the drill titles read it. */
export function lensLabel(lens: OperationsLensKey | undefined, pen: OperationsPenKey | undefined): string | null {
  const parts = [lens ? LENS_LABEL[lens] : null, pen ? PEN_LABEL[pen] : null].filter((p): p is string => p !== null);
  return parts.length === 0 ? null : parts.join(" · ");
}

/** " · Stuck · External only" for a title, "" without a lens. */
export function lensSuffix(lens: OperationsLensKey | undefined, pen: OperationsPenKey | undefined): string {
  const label = lensLabel(lens, pen);
  return label ? ` · ${label} only` : "";
}

/** CSS modifier used by tiles, pills and bar segments (shares the attention palette). */
export const ATTENTION_CLASS: Record<OperationsAttentionKey, string> = {
  stuck: "stuck",
  blocked: "blocked",
  stuckAndBlocked: "stuckAndBlocked",
  clear: "clear",
  unknown: "unknown",
};

const bd = (n: number) => `${n} business day${n === 1 ? "" : "s"}`;

// ---- Start date (Rafael, 2026-10-24): At risk / Breached, a third colour family (indigo / plum), never the attention palette.

export const START_RISK_ORDER: ("atRisk" | "breached")[] = ["atRisk", "breached"];

export const START_RISK_LABEL: Record<OperationsStartRiskKey, string> = {
  none: "No start date",
  onTime: "On time",
  atRisk: "At risk",
  breached: "Breached",
};

/** CSS modifier for the tiles, pills and chip dots. */
export const START_RISK_CLASS: Record<OperationsStartRiskKey, string> = {
  none: "none",
  onTime: "onTime",
  atRisk: "atRisk",
  breached: "breached",
};

export function startRiskTip(risk: "atRisk" | "breached", atRiskAfterBd: number, breachedAfterBd = 0): string {
  const unit = "An open onboarding journey (at intake awaiting its request, in pre-verification or in its request) or an extension whose work-order version is not yet approved.";
  const breachedWhen = breachedAfterBd > 0 ? `the version started ${bd(breachedAfterBd)} or more ago` : "the version starts today or already started";
  return risk === "atRisk"
    ? `${unit} At risk = the version starts within the next ${bd(atRiskAfterBd)}${breachedAfterBd > 0 ? `, or started less than ${bd(breachedAfterBd)} ago` : ""}. The version's start date, not the work order's: an extension's work order started months ago. Counted once per journey; approval ends it.`
    : `${unit} Breached = ${breachedWhen}. Counted once per journey; approval ends it.`;
}

/** "in 1 bd" / "starts today" / "3 bd late": the signed lead under a start date. */
export function startLeadLabel(lead: number | null | undefined): string {
  if (lead == null) return "";
  if (lead > 0) return `in ${lead} bd`;
  if (lead === 0) return "starts today";
  return `${-lead} bd late`;
}

/** The two clocks (Rafael, 2026-10-21), worded for the tiles, the lens and the Request Detail banner. */
export function attentionTip(state: OperationsAttentionKey, stuckAfterBd: number, blockedAfterBd: number): string {
  switch (state) {
    case "stuck":
      return `Nobody has touched it for more than ${bd(stuckAfterBd)}, but its task or work order moved within the last ${bd(blockedAfterBd)}.`;
    case "blocked":
      return `Its task or work order has not moved for more than ${bd(blockedAfterBd)}, although someone touched it within the last ${bd(stuckAfterBd)}.`;
    case "stuckAndBlocked":
      return `Untouched for more than ${bd(stuckAfterBd)} and not moving for more than ${bd(blockedAfterBd)}: nobody is on it and it is going nowhere.`;
    case "clear":
      return `Touched within the last ${bd(stuckAfterBd)} and moving within the last ${bd(blockedAfterBd)}.`;
    default:
      return "Task data was unavailable for this snapshot, so the state could not be worked out.";
  }
}

const STATUS_LABEL: Record<string, string> = {
  InProgress: "In Progress",
  PendingValidation: "Pending Validation",
  RequiresReview: "Requires Review",
  PendingOnboarding: "Pending Onboarding",
  OnHold: "On Hold",
  AwaitingConsultant: "Awaiting Consultant",
};

export function requestStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

const ROLE_LABEL: Record<string, string> = {
  Consultant: "Consultant",
  SubvendorWorker: "Subvendor worker",
  SubvendorOrg: "Subvendor",
  ClientService: "Client Service",
  AccountManager: "Account Manager",
  Internal: "Internal",
  Other: "Other",
};

export function actorRoleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role;
}

export const WORK_ORDER_STEP_LABEL: Record<string, string> = {
  "to submit": "to submit",
  "to approve": "to approve",
};

/** Who holds an item: "Client Service · K. Osei", "Work order · to approve", or "No pending action". */
export function waitingOnLabel(waitingOn: OperationsWaitingOnDto | null): string {
  if (!waitingOn) return "No pending action";
  if (waitingOn.kind === "workOrder") {
    return waitingOn.detail ? `Work order · ${WORK_ORDER_STEP_LABEL[waitingOn.detail] ?? waitingOn.detail}` : "Work order";
  }
  const role = waitingOn.role === "Other" && waitingOn.roleRaw ? waitingOn.roleRaw : actorRoleLabel(waitingOn.role ?? "");
  return waitingOn.detail ? `${role} · ${waitingOn.detail}` : role;
}

export const WORK_TYPE_LABEL: Record<string, string> = {
  PreOnboardManual: "Pre-Verification (manual)",
  PreOnboardClient: "Pre-Verification (client)",
  PreOnboardAts: "Pre-Verification (ATS)",
  PreOnboardVms: "Pre-Verification (VMS)",
};

export function workTypeLabel(workType: string): string {
  return WORK_TYPE_LABEL[workType] ?? workType;
}

/**
 * Work-type groups for colour: distinct hues that pass the colour-vision checks (see
 * docs/ClaudeDesign/age-histogram-options.html). The pre-verification types are journey members, so they
 * take the Onboarding hue wherever a record still carries that label; everything else (documents,
 * changes, unknown types) is "Other", drawn hatched.
 */
export interface WorkTypeGroup {
  key: "Onboarding" | "Redeployment" | "Extension" | "Other";
  label: string;
  color: string;
  hatch: boolean;
}

const GROUP_ONBOARDING: WorkTypeGroup = { key: "Onboarding", label: "Onboarding", color: "var(--ops-wt-onboarding)", hatch: false };
const GROUP_REDEPLOYMENT: WorkTypeGroup = { key: "Redeployment", label: "Redeployment", color: "var(--ops-wt-redeployment)", hatch: false };
const GROUP_EXTENSION: WorkTypeGroup = { key: "Extension", label: "Extension", color: "var(--ops-wt-extension)", hatch: false };
const GROUP_OTHER: WorkTypeGroup = { key: "Other", label: "Other (documents, changes)", color: "var(--ops-wt-other)", hatch: true };

export const WORK_TYPE_GROUPS: readonly WorkTypeGroup[] = [
  GROUP_ONBOARDING, GROUP_REDEPLOYMENT, GROUP_EXTENSION, GROUP_OTHER,
];

export function workTypeGroup(workType: string): WorkTypeGroup {
  switch (workType) {
    case "Onboarding": return GROUP_ONBOARDING;
    case "Redeployment": return GROUP_REDEPLOYMENT;
    case "Extension": return GROUP_EXTENSION;
    default: return workType.startsWith("PreOnboard") ? GROUP_ONBOARDING : GROUP_OTHER;
  }
}

/** Colour of a work type's group, so the Flow dots and the age legend agree. */
export function workTypeColor(workType: string): string {
  return workTypeGroup(workType).color;
}

/** "+3 ↑" / "−2 ↓" / "0 →" */
export function formatDelta(delta: number): string {
  if (delta > 0) return `+${delta} ↑`;
  if (delta < 0) return `${delta} ↓`;
  return "0 →";
}

export function deltaClass(delta: number): string {
  return delta > 0 ? "up" : delta < 0 ? "down" : "flat";
}

export const CHECK_LABEL: Record<string, string> = {
  stage_totals_equal_open: "Stage totals",
  attention_sum_equals_open: "Attention sum",
  flow_total_equals_open: "Flow total",
  age_sample_equals_flow_open: "Age sample",
  flow_identity: "Flow identity",
  owner_sum_equals_open: "Owner sum",
  owner_workload_journeys_equal_flow_open: "Owner workload",
  owner_start_risk_equals_tiles: "Start-date owners",
  owner_groups_equal_items: "Owner groups",
  status_split_equals_request_processing: "Status split",
};

/** The stage card's first row: intakes that do not exist in FBO yet. */
export const INTAKE_ROW_LABEL = "Request (Intake)";

export const INTAKE_SOURCE_LABEL: Record<OperationsIntakeSourceKey, string> = { ats: "ATS", client: "Client", manual: "Manual", vms: "VMS" };

export const INTAKE_STATE_LABEL: Record<string, string> = { notInFbo: "Not in FBO yet", awaitingRequest: "WO created, awaiting request" };

/** Which record carries a journey right now (the journey drawer's Carrier chip and column). */
export const JOURNEY_CARRIER_LABEL: Record<OperationsJourneyCarrierKey, string> = {
  intake: "At intake",
  preOnboarding: "In pre-verification",
  awaitingRequest: "WO created, awaiting request",
  request: "In request",
  workOrder: "Offboarding",
  none: "No open record",
};

/** The Flow card's Onboarding and Redeployment rows count journeys, not records (Rafael, 2026-10-20). */
/** The Extension, ChangeRequest, Termination and Other rows (every intake type is synced since 2026-10-22). */
export const TYPED_ROW_TIP =
  "One journey per intake request of this type and the FBO request of the same type on its work order; either one alone counts too. " +
  "Ended = the request completed, or a lone intake accepted.";

export const JOURNEY_ROW_TIP =
  "One journey per consultant: the intake, the pre-verification and the onboarding request together. " +
  "Open while any of them is open; Started at the first record; Ended when the work order is approved. " +
  "A journey moves to the Redeployment row once its request classifies as one.";


export function intakeRowTip(stuckAfterBd: number, blockedAfterBd: number, hideAfterUntouchedBd = 0): string {
  return (
    "Intake requests (ATS placements; Client, Manual and VMS requests) that are still waiting for FBO: nothing created yet, or a work order with no request. " +
    `Stuck: untouched for more than ${bd(stuckAfterBd)}. Blocked: status unchanged for more than ${bd(blockedAfterBd)}. ` +
    (hideAfterUntouchedBd > 0 ? `Intakes not yet in FBO and untouched for more than ${bd(hideAfterUntouchedBd)} are not shown anywhere on this dashboard; an accepted intake waiting on its work order always is. ` : "") +
    "Not part of Open now; the stages below add up without it. Client, Assigned to and Request type apply; the other slicers empty it."
  );
}
